<script setup lang="ts">
/**
 * API Keys page (T03 polish — under sidebar layout).
 *
 * Layout strategy:
 * - Full-width content area (no `max-w-*` wrapper) per T03 spec.
 * - Page header on top: title + subtitle on the left, "+ Create Key" on the right.
 * - Single table card listing ALL keys (active + revoked) so users see the full
 *   lifecycle in one place. STATUS column distinguishes them; delete icon is
 *   only rendered for active rows (revoked keys can't be revoked again).
 * - Empty state when the user has no keys at all.
 *
 * Backend contract (verified in keys.controller.ts dehydrate):
 *   { id, prefix, suffix, name, openrouter_enabled, last_used_at, revoked_at, created_at }
 */
import { onMounted, ref, computed } from 'vue';
import { useKeysStore } from '../stores/keys';
import { formatDateTime } from '../lib/format';
import KeyCreateModal from '../components/KeyCreateModal.vue';
import type { ApiKey } from '../api/keys';

const keys = useKeysStore();
const showCreate = ref(false);
const pendingRevokeId = ref<string | null>(null);
const errorBanner = ref<string | null>(null);

const hasKeys = computed(() => keys.items.length > 0);
const isInitialLoad = computed(() => keys.loading && keys.items.length === 0);

onMounted(() => keys.load());

async function revoke(k: ApiKey): Promise<void> {
  if (!window.confirm(`Revoke key ${k.prefix}…${k.suffix}? This cannot be undone.`)) return;
  pendingRevokeId.value = k.id;
  errorBanner.value = null;
  try {
    await keys.revoke(k.id);
  } catch (err) {
    errorBanner.value = (err as Error).message;
  } finally {
    pendingRevokeId.value = null;
  }
}

/** "sk-9c126056…••••••" style masked key per reference image. */
function maskedKey(k: ApiKey): string {
  return `${k.prefix}…••••••`;
}

function formatLastUsed(iso: string | null | undefined): string {
  // Match reference: explicit "Never" instead of em-dash when no usage yet.
  if (!iso) return 'Never';
  return formatDateTime(iso);
}
</script>

<template>
  <div class="px-6 lg:px-10 py-8 flex flex-col gap-6">
    <!-- Page header -->
    <header class="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 class="text-2xl font-semibold text-ink">API Keys</h1>
        <p class="text-sm text-muted mt-1">
          Manage your API keys for authenticating proxy requests.
        </p>
      </div>
      <button
        type="button"
        class="btn-primary text-sm"
        @click="showCreate = true"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.25"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Create Key
      </button>
    </header>

    <!-- Error banner -->
    <div v-if="errorBanner" class="card p-3 border-danger text-danger text-sm">
      {{ errorBanner }}
    </div>

    <!-- Initial loading -->
    <section v-if="isInitialLoad" class="card p-6 text-muted text-sm">
      Loading…
    </section>

    <!-- Empty state -->
    <section
      v-else-if="!hasKeys"
      class="card p-10 flex flex-col items-center text-center"
    >
      <div class="text-base font-medium text-ink">No API keys yet</div>
      <p class="text-sm text-muted mt-2 max-w-md">
        Create your first key to start sending requests to the proxy. The raw
        key is shown once at creation — store it somewhere safe.
      </p>
      <button class="btn-primary mt-5" @click="showCreate = true">
        + Create Key
      </button>
    </section>

    <!-- Keys table -->
    <section v-else class="card overflow-hidden">
      <table class="w-full table">
        <thead>
          <tr>
            <th class="w-[14%]">Name</th>
            <th class="w-[22%]">Key</th>
            <th class="w-[20%]">Created</th>
            <th class="w-[20%]">Last used</th>
            <th class="w-[12%]">Status</th>
            <th class="w-[6%] text-right"><span class="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="k in keys.items" :key="k.id" class="group">
            <td class="font-medium text-ink">{{ k.name ?? '—' }}</td>
            <td>
              <code class="font-mono text-xs text-ink">{{ maskedKey(k) }}</code>
            </td>
            <td class="text-muted">{{ formatDateTime(k.created_at) }}</td>
            <td class="text-muted">{{ formatLastUsed(k.last_used_at) }}</td>
            <td>
              <span
                v-if="!k.revoked_at"
                class="inline-flex items-center text-[11px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-full border bg-surface-soft border-ok/40 text-ok"
              >
                Active
              </span>
              <span
                v-else
                class="inline-flex items-center text-[11px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-full border bg-surface-soft border-danger/30 text-danger"
              >
                Revoked
              </span>
            </td>
            <td class="text-right">
              <button
                v-if="!k.revoked_at"
                type="button"
                class="inline-flex items-center justify-center p-2 rounded-md text-muted hover:text-danger hover:bg-danger/10 border border-border transition disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="pendingRevokeId === k.id"
                :aria-label="`Revoke key ${k.prefix}`"
                :title="pendingRevokeId === k.id ? 'Revoking…' : 'Revoke key'"
                @click="revoke(k)"
              >
                <svg
                  v-if="pendingRevokeId !== k.id"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
                <svg
                  v-else
                  class="animate-spin"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <KeyCreateModal
      v-if="showCreate"
      @close="
        showCreate = false;
        keys.load();
      "
    />
  </div>
</template>
